import { Box } from '@mui/material'
import { FC, RefObject, useCallback, useEffect, useRef, useState } from 'react'

interface ShiftScheduleHorizontalScrollbarProps {
	scrollRef: RefObject<HTMLDivElement | null>
	visible?: boolean
}

export const ShiftScheduleHorizontalScrollbar: FC<
	ShiftScheduleHorizontalScrollbarProps
> = ({ scrollRef, visible = true }) => {
	const trackRef = useRef<HTMLDivElement>(null)
	const isDraggingRef = useRef(false)
	const dragStartXRef = useRef(0)
	const dragStartScrollLeftRef = useRef(0)
	const [metrics, setMetrics] = useState({
		scrollWidth: 0,
		clientWidth: 0,
		scrollLeft: 0,
	})

	const updateMetrics = useCallback(() => {
		const element = scrollRef.current
		if (!element) return

		setMetrics({
			scrollWidth: element.scrollWidth,
			clientWidth: element.clientWidth,
			scrollLeft: element.scrollLeft,
		})
	}, [scrollRef])

	useEffect(() => {
		const element = scrollRef.current
		if (!element) return

		updateMetrics()
		element.addEventListener('scroll', updateMetrics)

		const observer = new ResizeObserver(updateMetrics)
		observer.observe(element)

		return () => {
			element.removeEventListener('scroll', updateMetrics)
			observer.disconnect()
		}
	}, [scrollRef, updateMetrics, visible])

	const maxScroll = Math.max(metrics.scrollWidth - metrics.clientWidth, 0)
	const canScroll = visible && maxScroll > 0
	const thumbWidthPercent =
		metrics.scrollWidth > 0
			? (metrics.clientWidth / metrics.scrollWidth) * 100
			: 100
	const thumbLeftPercent =
		maxScroll > 0
			? (metrics.scrollLeft / maxScroll) * (100 - thumbWidthPercent)
			: 0

	const handleTrackClick = (event: React.MouseEvent<HTMLDivElement>) => {
		const element = scrollRef.current
		const track = trackRef.current
		if (!element || !track || maxScroll <= 0) return

		const rect = track.getBoundingClientRect()
		const clickRatio = (event.clientX - rect.left) / rect.width
		element.scrollLeft = clickRatio * maxScroll
	}

	const handleThumbMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
		event.preventDefault()
		event.stopPropagation()

		const element = scrollRef.current
		const track = trackRef.current
		if (!element || !track || maxScroll <= 0) return

		isDraggingRef.current = true
		dragStartXRef.current = event.clientX
		dragStartScrollLeftRef.current = element.scrollLeft

		const thumbTravel = track.clientWidth * (1 - thumbWidthPercent / 100)

		const handleMouseMove = (moveEvent: MouseEvent) => {
			if (!isDraggingRef.current || !scrollRef.current) return

			const deltaX = moveEvent.clientX - dragStartXRef.current
			const scrollDelta =
				thumbTravel > 0 ? (deltaX / thumbTravel) * maxScroll : 0

			scrollRef.current.scrollLeft = Math.min(
				maxScroll,
				Math.max(0, dragStartScrollLeftRef.current + scrollDelta),
			)
		}

		const handleMouseUp = () => {
			isDraggingRef.current = false
			document.removeEventListener('mousemove', handleMouseMove)
			document.removeEventListener('mouseup', handleMouseUp)
		}

		document.addEventListener('mousemove', handleMouseMove)
		document.addEventListener('mouseup', handleMouseUp)
	}

	if (!canScroll) return null

	return (
		<Box sx={{ pl: '12px', pt: '8px', pb: '4px', flexShrink: 0 }}>
			<Box
				ref={trackRef}
				onClick={handleTrackClick}
				sx={{
					height: '4px',
					backgroundColor: '#ECECEC',
					borderRadius: '2px',
					position: 'relative',
					cursor: 'pointer',
				}}
			>
				<Box
					onMouseDown={handleThumbMouseDown}
					sx={{
						position: 'absolute',
						top: 0,
						left: `${thumbLeftPercent}%`,
						width: `${thumbWidthPercent}%`,
						height: '4px',
						backgroundColor: '#B8B8B8',
						borderRadius: '2px',
						cursor: 'grab',
						'&:hover': {
							backgroundColor: '#9E9E9E',
						},
					}}
				/>
			</Box>
		</Box>
	)
}
